import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { StatusChip, nextStepText } from "@/components/RoadmapStrip";
import PortalPrograms from "@/pages/portal/PortalPrograms";
import PortalTrainingProgram from "@/pages/portal/PortalTrainingProgram";
import AcquisitionJourney from "@/pages/portal/acquisition/AcquisitionJourney";
import { RetailRoadmap, useRetailRoadmap } from "@/components/roadmap/RetailRoadmap";
import { MyChecklist } from "@/components/roadmap/MyChecklist";
import { ProgramRoadmapImage } from "@/components/roadmap/ProgramRoadmapImage";
import { ProgramMilestoneJourney } from "@/components/roadmap/ProgramMilestones";
import { programLabel, taskCounts, isEnded, ENDED_LABEL } from "@/pages/portal/portalUtils";
import { ArrowRight, CheckCircle2, Sparkles, Info, Circle, Clock, UserRound, Users, AlertTriangle } from "lucide-react";

export default function PortalJourney({ sub, onGoToTeam, goTo }) {
  if (sub === "programs") return <ProgramsView onGoToTeam={onGoToTeam} />;
  if (sub === "training") return <PortalTrainingProgram goTo={goTo} />;
  if (sub === "acquisition") return <AcquisitionJourney goTo={goTo} />;
  return <JourneyRoadmap goTo={goTo} checklist={sub === "checklist"} />;
}

function JourneyRoadmap({ goTo, checklist }) {
  const [pj, setPj] = useState(undefined);
  const [legacy, setLegacy] = useState(false);
  useEffect(() => { api.get("/portal/program-journey").then((r) => setPj(r.data)).catch(() => setPj({ project: null })); }, []);
  if (checklist) return <MyChecklist goTo={goTo} />;
  if (pj === undefined) return <p className="text-sm text-slate-400">Loading your journey…</p>;
  if (!pj.project) return <ProgramRoadmap goTo={goTo} />;
  return (
    <div className="space-y-6">
      <ProgramMilestoneJourney data={pj} onOpenTask={(id) => goTo("tasks", null, { taskId: id })} />
      <button onClick={() => setLegacy(!legacy)} className="text-sm font-medium text-[#006090] hover:underline" data-testid="pj-toggle-requirements">{legacy ? "Hide program requirements" : "View official program requirements"}</button>
      {legacy && <ProgramRoadmap goTo={goTo} />}
    </div>
  );
}

function ProgramRoadmap({ goTo }) {
  const [info, setInfo] = useState(undefined);
  useEffect(() => { api.get("/portal/journey-roadmap").then((r) => setInfo(r.data)).catch(() => setInfo({ program_key: null })); }, []);
  if (info === undefined) return <p className="text-sm text-slate-400">Loading your journey…</p>;
  if (info.program_key === "retail_traditional" && !info.image_id) return <RtRoadmap goTo={goTo} info={info} />;
  return <ProgramRoadmapImage data={info} />;
}

function RtRoadmap({ goTo, info }) {
  const [rt] = useRetailRoadmap();
  if (rt === undefined) return <p className="text-sm text-slate-400">Loading your journey…</p>;
  return rt.applicable ? <RetailRoadmap data={rt} goTo={goTo} /> : <ProgramRoadmapImage data={info} />;
}

function ProgramsView({ onGoToTeam }) {
  const [active, setActive] = useState(null);
  const open = (slug) => { setActive(slug); window.scrollTo({ top: 0, behavior: "smooth" }); };
  return (
    <div className="space-y-10">
      <PortalPrograms onGoToTeam={onGoToTeam} active={active} setActive={open} />
      {!active && <PathCards onOpen={open} />}
    </div>
  );
}

function RoadmapView() {
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => {
    api.get("/portal/roadmap").then((r) => setData(r.data)).catch(() => setData({ enrollment: null }));
    api.get("/portal/tasks").then((r) => setTasks(r.data.tasks || [])).catch(() => setTasks([]));
  }, []);
  if (data === null) return <p className="text-sm text-slate-400">Loading your journey…</p>;
  if (!data.enrollment) return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="portal-roadmap-empty">
      <p className="text-sm text-slate-500">Your program roadmap will appear here once your recruiter sets it up. In the meantime, explore the programs available to you.</p>
    </div>
  );
  const phases = data.phases || [];
  const selected = phases.find((p) => p.id === sel) || phases.find((p) => p.is_current) || phases[0];
  return (
    <div className="space-y-5" data-testid="portal-roadmap">
      {isEnded(data.enrollment) && (
        <div className="rounded-xl border border-slate-300 bg-slate-100 p-4" data-testid="journey-ended">
          <p className="font-semibold text-slate-900 text-sm">{ENDED_LABEL[data.enrollment.status]}</p>
          <p className="text-xs text-slate-600 mt-0.5">This program is no longer active. Your completed history is shown below for reference.</p>
        </div>
      )}
      <JourneyHero data={data} tasks={tasks} />
      <PhaseStepper phases={phases} selectedId={selected?.id} onSelect={setSel} />
      {data.mentor_name && (
        <div className="rounded-xl border border-[#0D9488]/20 bg-[#0D9488]/5 p-4" data-testid="portal-mentor">
          <p className="text-xs font-semibold text-slate-500 uppercase">Your Mentor</p>
          <p className="mt-0.5 font-semibold text-slate-900">{data.mentor_name}{data.mentor_agency_name ? ` · ${data.mentor_agency_name}` : ""}</p>
        </div>
      )}
      <Gate gate={data.reserve_gate} testId="portal-reserve-gate" blockId="portal-reserve-block" tone="sky"
        ready="You're ready for your Reserve Appointment" working="Working toward your Reserve Appointment"
        readyText="Great work — your recruiting team will take it from here." workingText="A few items still need to be completed and verified:" />
      <Gate gate={data.career_gate} testId="portal-career-gate" blockId="portal-career-block" tone="violet"
        ready="You're ready for Career Conversion" working="Working toward your Career Conversion"
        readyText="Great work — your recruiting team will prepare your New Agency Appointment." workingText="A few items still need to be completed and verified before you convert to a career agency:" />
      {selected && <PhaseDetail phase={selected} data={data} tasks={tasks} />}
    </div>
  );
}

function CountPill({ label, c, testId }) {
  return (
    <div className="rounded-xl bg-white/10 px-3 py-2" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-white/60">{label}</p>
      <p className="font-semibold">{c.done} of {c.total}</p>
    </div>
  );
}

function JourneyHero({ data, tasks }) {
  const pct = data.configured === false ? 0 : (data.progress || 0);
  const current = (data.phases || []).find((p) => p.is_current);
  const celebrate = data.configured !== false && (pct >= 100 || data.ready_for_review);
  return (
    <div className="space-y-3" data-testid="portal-journey">
      <div className="rounded-2xl bg-[#013A5A] text-white p-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#0D9488]/20 blur-2xl" />
        <p className="text-[#5EEAD4] text-xs font-semibold uppercase tracking-wider">Your Path to Agency Ownership</p>
        <h3 className="font-display text-2xl font-bold mt-0.5" data-testid="journey-program">{programLabel(data.enrollment)}</h3>
        <div className="mt-4 flex items-end justify-between gap-4 flex-wrap">
          <div className="min-w-[180px]">
            <p className="text-xs text-white/60">Current stage</p>
            <p className="font-semibold text-lg leading-tight" data-testid="journey-current-stage">{current?.name || "—"}</p>
            <p className="text-xs text-white/70 mt-1 flex items-center gap-1" data-testid="journey-next-step">
              <ArrowRight className="w-3.5 h-3.5" />
              {nextStepText(data, true)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold leading-none" data-testid="journey-progress">{pct}%</p>
            <p className="text-xs text-white/70 mt-1">{data.required_complete}/{data.required_total} required milestones verified</p>
          </div>
        </div>
        <div className="mt-4 h-2 bg-white/15 rounded-full overflow-hidden"><div className="h-full bg-[#0D9488] rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
          <CountPill label="Program Requirements" c={{ done: data.required_complete, total: data.required_total }} testId="journey-count-requirements" />
          <CountPill label="Your Tasks" c={taskCounts(tasks, "candidate")} testId="journey-count-mine" />
          <CountPill label="District Team Tasks" c={taskCounts(tasks, "district")} testId="journey-count-district" />
        </div>
      </div>
      <p className="text-xs text-slate-500 flex items-start gap-1.5" data-testid="journey-progress-note"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />Official progress counts required program milestones verified by your district team. Completing tasks helps you get there, but tasks don't change this number on their own.</p>
      {celebrate && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="journey-celebrate">
          <Sparkles className="w-5 h-5 text-emerald-600 shrink-0" />
          <p className="text-sm text-emerald-800 font-medium">Incredible progress — you've completed the required steps on your path. Your recruiting team will guide you to the next milestone.</p>
        </div>
      )}
    </div>
  );
}

const NODE = (p) => p.status === "complete" ? "bg-emerald-500 border-emerald-500 text-white"
  : p.is_current ? "bg-[#006090] border-[#006090] text-white"
  : p.status === "in_progress" ? "bg-white border-amber-400 text-amber-600" : "bg-white border-slate-300 text-slate-400";

function PhaseStepper({ phases, selectedId, onSelect }) {
  if (!phases.length) return null;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 overflow-x-auto tr-scroll" data-testid="journey-stepper">
      <ol className="flex items-start min-w-max gap-0">
        {phases.map((p, i) => (
          <li key={p.id} className="flex items-start">
            <button onClick={() => onSelect(p.id)} data-testid={`journey-step-${p.id}`} aria-current={p.is_current ? "step" : undefined}
              className={`flex flex-col items-center w-32 text-center rounded-xl py-2 transition-colors ${selectedId === p.id ? "bg-slate-100" : "hover:bg-slate-50"}`}>
              <span className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-xs font-bold ${NODE(p)}`}>{p.status === "complete" ? <CheckCircle2 className="w-4 h-4" /> : i + 1}</span>
              <span className={`mt-1.5 text-[11px] leading-tight px-1 ${p.is_current ? "font-bold text-[#006090]" : "text-slate-600"}`}>{p.name}</span>
              <span className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400">{p.is_current ? "You are here" : p.status === "complete" ? "Completed" : "Upcoming"}</span>
            </button>
            {i < phases.length - 1 && <span className={`h-0.5 w-6 mt-6 -mx-3 ${p.status === "complete" ? "bg-emerald-300" : "bg-slate-200"}`} />}
          </li>
        ))}
      </ol>
    </div>
  );
}

function Gate({ gate, testId, blockId, tone, ready, working, readyText, workingText }) {
  if (!gate) return null;
  const cls = gate.ready ? "border-emerald-200 bg-emerald-50" : tone === "sky" ? "border-sky-200 bg-sky-50" : "border-violet-200 bg-violet-50";
  const dot = tone === "sky" ? "bg-sky-400" : "bg-violet-400";
  return (
    <div className={`rounded-xl border p-4 ${cls}`} data-testid={testId}>
      <p className="font-semibold text-slate-900 text-sm">{gate.ready ? ready : working}</p>
      <p className="text-xs text-slate-600 mt-0.5">{gate.ready ? readyText : workingText}</p>
      {!gate.ready && gate.blocking?.length > 0 && (
        <ul className="mt-2 space-y-1">{gate.blocking.map((b) => (
          <li key={b} className="text-xs text-slate-700 flex items-center gap-1.5" data-testid={blockId}><span className={`w-1.5 h-1.5 rounded-full ${dot}`} /> {b}</li>))}</ul>
      )}
    </div>
  );
}

const TASK_STATE = (t, phase) => t.is_done ? { label: "Completed", Icon: CheckCircle2, cls: "text-emerald-600" }
  : t.is_planned || (!phase.is_current && phase.status !== "complete") ? { label: "Upcoming", Icon: Circle, cls: "text-slate-400" }
  : t.status === "needs_attention" ? { label: "Needs Attention", Icon: AlertTriangle, cls: "text-rose-600" }
  : t.status === "submitted" ? { label: "Submitted", Icon: Clock, cls: "text-[#006090]" }
  : t.status === "in_progress" ? { label: "In Process", Icon: Clock, cls: "text-amber-600" }
  : { label: "Current", Icon: Circle, cls: "text-[#006090]" };

function TaskList({ title, Icon, items, phase, testId, empty, district }) {
  return (
    <div data-testid={testId}>
      <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5 mb-2"><Icon className="w-3.5 h-3.5" />{title} <span className="text-slate-400 font-medium">{items.filter((t) => t.is_done).length}/{items.length}</span></h5>
      {items.length === 0 ? <p className="text-xs text-slate-400">{empty}</p> : (
        <ul className="space-y-1.5">{items.map((t) => {
          const s = TASK_STATE(t, phase);
          return (
            <li key={t.id} className="flex items-start gap-2 rounded-lg bg-slate-50 px-2.5 py-2" data-testid={`journey-task-${t.id}`}>
              <s.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${s.cls}`} />
              <div className="min-w-0 flex-1">
                <p className={`text-sm ${t.is_done ? "text-slate-500" : "text-slate-800 font-medium"}`}>{t.display_title || t.title}</p>
                {district && t.assignee_name && !t.is_done && <p className="text-[11px] text-slate-500">Assigned to {t.assignee_name}</p>}
              </div>
              <span className={`text-[10px] font-semibold uppercase shrink-0 ${s.cls}`}>{s.label}</span>
            </li>
          );
        })}</ul>
      )}
    </div>
  );
}

function PhaseDetail({ phase: p, data, tasks }) {
  const nextIds = new Set((data.next_actions || []).map((r) => r.requirement_id));
  const inPhase = tasks.filter((t) => t.phase === p.name && t.status !== "cancelled");
  return (
    <div className={`rounded-2xl border bg-white p-5 ${p.is_current ? "border-[#006090] ring-1 ring-[#006090]/20" : "border-slate-200"}`} data-testid="journey-timeline">
      <div data-testid={`journey-phase-${p.id}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold text-slate-900">{p.name}</h3>
            {p.is_current && <span className="text-[10px] font-bold text-[#006090] uppercase bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5" data-testid="journey-here">You are here</span>}
            {!p.is_current && <span className="text-[10px] font-bold uppercase text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{p.status === "complete" ? "Completed" : "Upcoming"}</span>}
          </div>
          <span className="text-xs text-slate-500">{p.required_complete}/{p.required_total} requirements{p.pct == null ? "" : ` · ${p.pct}%`}</span>
        </div>
        {(p.candidate_description || p.description) && <p className="text-sm text-slate-600 mt-2" data-testid="journey-phase-description">{p.candidate_description || p.description}</p>}
        {p.pct != null && <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${p.status === "complete" ? "bg-emerald-500" : "bg-[#006090]"}`} style={{ width: `${p.pct}%` }} /></div>}
        <div className="mt-5 grid md:grid-cols-2 gap-5">
          <TaskList title="Your Tasks" Icon={UserRound} items={inPhase.filter((t) => t.owner === "candidate")} phase={p} testId="journey-your-tasks" empty="No tasks for you in this stage." />
          <TaskList title="District Team Tasks" Icon={Users} items={inPhase.filter((t) => t.owner === "district")} phase={p} testId="journey-district-tasks" empty="No district team tasks in this stage." district />
        </div>
        {(p.requirements || []).length > 0 && (
          <div className="mt-6">
            <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Program Requirements</h5>
            <ul className="space-y-2">{p.requirements.map((r) => {
              const isNext = nextIds.has(r.requirement_id);
              return (
                <li key={r.requirement_id} data-testid={`journey-req-${r.requirement_id}`} className={`rounded-lg p-2.5 ${isNext ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5 flex-wrap">{r.title}{isNext && <span className="text-[10px] font-bold text-amber-700 uppercase">Do next</span>}</p>
                      {r.candidate_instructions && <p className="text-xs text-slate-500 mt-0.5">{r.candidate_instructions}</p>}
                      {r.gated && <p className="text-[11px] text-slate-400 mt-0.5">{r.gate_reason}</p>}
                    </div>
                    <StatusChip status={r.status} />
                  </div>
                </li>
              );
            })}</ul>
          </div>
        )}
      </div>
    </div>
  );
}

const PATHS = [
  { keys: ["buy_agency"], slug: "retail-acquisition", label: "Buy an Existing Agency", img: "/home/buy-agency.jpg", blurb: "Acquire an established book of business and step into ownership with full support." },
  { keys: ["start_agency", "agency_owner"], slug: "retail-traditional", label: "Start an Agency from Scratch", img: "/home/start-agency.jpg", blurb: "Build your own Farmers agency from the ground up with a proven playbook." },
  { keys: ["work_in_agency", "work_for_agency"], slug: "producer", label: "Work in an Agency", img: "/home/work-in-agency.jpg", blurb: "Grow your insurance career inside an established Farmers agency." },
];

function PathCards({ onOpen }) {
  const [interest, setInterest] = useState(null);
  useEffect(() => { api.get("/portal/summary").then((r) => setInterest(r.data.interest)).catch(() => {}); }, []);
  return (
    <section data-testid="portal-paths">
      <h2 className="font-display text-lg font-semibold text-slate-900">Your Path with Farmers</h2>
      <div className="mt-4 grid sm:grid-cols-3 gap-5">
        {PATHS.map((p) => {
          const mine = p.keys.includes(interest);
          return (
            <button type="button" key={p.label} onClick={() => onOpen(p.slug)} data-testid={`portal-path-${p.keys[0]}`} className={`group text-left rounded-2xl overflow-hidden border bg-white transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#006090] ${mine ? "border-[#006090] ring-2 ring-[#006090]/30 shadow-md" : "border-slate-200 hover:border-[#006090]"}`}>
              <div className="relative h-36 overflow-hidden">
                <img src={p.img} alt={p.label} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                {mine && <span className="absolute top-2 left-2 text-[11px] font-semibold bg-[#006090] text-white rounded-full px-2 py-0.5">Your Path</span>}
              </div>
              <div className="p-4"><h3 className="font-display font-semibold text-slate-900 text-sm">{p.label}</h3><p className="mt-1 text-xs text-slate-600 leading-snug">{p.blurb}</p><span className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-[#006090]">Learn more <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" /></span></div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
